import { ActionIcon, AppShell, Box, Button, createStyles, Divider, Group, Header, Input, Kbd, Modal, Navbar, Popover, Select, Stack, Text, ThemeIcon, Title, UnstyledButton } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowLeft, IconArrowsMaximize, IconChevronDown, IconChevronRight, IconInfoCircle, IconSearch, IconSelector } from '@tabler/icons-react';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ApprovalImageToolbarModes, ApprovalLayoutTypes, FlagRating, SessionTypes } from 'utils/constants';

import BottomTray from './components/BottomTray';
import MainPanel from './components/MainPanel';
import Toolbar from './components/Toolbar';
import {
    setApprovalSettings,
    setSelectedItem,
    setSelectedItems,
    setComparisonItems,
    setSelectedCompareFrame,
    setCaptures,
    fetchCaptures,
    setFilter,
    clearNotifications,
    setCapturesSuccess,
    getCapturesSuccess,
    refreshState,
    fetchLatestCaptures,
    getStyles,
    getStylesSuccess,
    fetchTalent,
} from './approvalSlice';

import { useFullscreen } from '@mantine/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Shortcuts from './components/Shortcuts';
import InfoPanel from '../../core/components/capture/InfoPanel';

import { clear } from 'idb-keyval';
import { showNotification } from '@mantine/notifications';
import { selectSortedCaptures } from './approvalSelectors';
import FilterPanel from './components/FilterPanel';
import { setLoading } from 'app/appSlice';
import { clearLocalStorage } from 'app/store';
import { setUser } from 'modules/Auth/authSlice';

import { CaptureFilterModel } from 'core/models/CaptureFilterModel';
import { CaptureEventType } from 'core/components/capture/CaptureThumb';
import { Crumbline } from 'core/components';
import { logger } from 'utils/logger';
import { getCompany, handleZipDownload, isPureObject } from 'utils/helper';
import moment from 'moment';
import { closeAllModals, ModalsProvider, openModal } from '@mantine/modals';
import TagTalent from '../../core/components/capture/TagTalent';
import { getSessions } from 'services/sessionService';

var _ = require('lodash');

const useStyles = createStyles((theme, _params, getRef) => ({
    shell: {
        '.info-panel': {
            width: 0,
            overflow: 'hidden',
            transition: 'width .2s',
            visibility: 'none',
        },
        '&.info-open': {
            '.info-panel': {
                maxWidth: 400,
                width: 400,
            },
        },

        '.filter-panel': {
            left: -350,
            width: 350,
            overflow: 'hidden',
            transition: 'left .2s ease-in-out',
            visibility: 'none',
        },
        '&.filter-open': {
            '.filter-panel': {
                left: 0,
                width: 350,
            },
            '.mantine-AppShell-main': {
                paddingLeft: 350,
            },
        },
    },
    container: {
        flexGrow: 1,
        ".approvaltoolbar[data-layouttype='compare']": {
            "&[data-selectedframe='1']": {
                '.openseacontrols': {
                    opacity: 0.1,
                    display: 'none',
                },
                '.openseacontrols:last-of-type': {
                    opacity: 1,
                    display: 'inline',
                },
            },
            "&[data-selectedframe='0']": {
                '.openseacontrols': {
                    opacity: 0.1,
                    display: 'none',
                },
                '#openSeaDragon1-controller': {
                    opacity: '1 !important',
                    display: 'inline',
                },
            },
        },
        '.openseadragon-canvas:focus, .openseadragon-canvas:focus-visible, .openseadragon-container:focus, .openseadragon-container:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none !important',
        },
    },
    keywordSearch: {
        input: {
            paddingRight: 67,
        },
        span: {
            fontSize: 10,
        },
        '.mantine-Input-rightSection': {
            width: 67,
        },
    },
    captureMenuItem: {
        marginLeft: theme.spacing.xl * 1.2,
        flexGrow: '1 !important',
        borderRadius: theme.radius.xs,
        paddingTop: theme.spacing.xs / 3,
        paddingBottom: theme.spacing.xs / 3,
        paddingLeft: theme.spacing.xs,
        // width:'100%',
        '&:hover': {
            backgroundColor: theme.colors.dark[4],
        },
        '&.selected': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[4],
            color: theme.colors.brand[4],
        },
    },
    crumbSelect: {
        // width: 150,
        border: 0,
        '& input': {
            border: 0,
            backgroundColor: 'transparent',
        },
    },
}));

const ApprovalModule = ({
    user,
    filteredSelections,
    captures,
    settings,
    selectedItem,
    selectedItems,
    comparisonItems,
    aggData,
    styles,
    talents,
    action,
    success,
    error,
    approvalFilter,
    setApprovalSettings,
    setSelectedItems,
    setSelectedItem,
    setComparisonItems,
    setSelectedCompareFrame,
    selectedCompareFrame,
    setFilter,
    setUser,
    isLoading,
    appLoading,
    setLoading,
    previousRoute,
    searchCursor,
}) => {
    const { toggle } = useFullscreen();
    const navigate = useNavigate();
    const [imageControlSettings, setImageControlSettings] = useState({ zoomDirection: 1 });
    const [infoShown, setInfoShown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // hash lookup for image locations.
    const imageLocHash = useRef({});

    const { classes } = useStyles();
    const dispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const filterKeys = ['client', 'catalogue', 'sessionId', 'star', 'sak', 'tags', 'machineId', 'styleNumber', 'colourTag'];

    const hasFetchedStyles = useRef(false);

    const [sessionData, setSessionData] = useState([]);

    const [scrollToTarget, setScrollToTarget] = useState(null);

    const selectAll = () => {
        setSelectedItems([...captures]);
    };

    // clear the cache
    useEffect(() => {
        clearLocalStorage();
        // need to clear the captures and selected captures.
        dispatch(refreshState(null, user));

        return () => {
            dispatch(setFilter(new CaptureFilterModel()));
            hasFetchedStyles.current = false;
        };
    }, []);

    // SUCCESS AND ERROR HANDLERS
    useEffect(() => {
        if (error) {
            showNotification({
                title: 'Whoops something went wrong.',
                message: error.message,
                position: 'top-center',
                color: 'red',
            });
            setLoading(false);
            setIsSearching(false);
            dispatch(clearNotifications());
        }

        if (success && success.type === getCapturesSuccess.toString()) {
            setLoading(false);
            setIsSearching(false);
        }
    }, [error, success, dispatch]);

    useEffect(() => {
        // if the form values styleNumber is not in the styles array then set the form value to the first style number
        if (styles && styles.length && form.values.styleNumber && !styles.find((val) => val.targetSort === form.values.styleNumber)) {
            form.setFieldValue('styleNumber', styles[0].stylenumber);
            logger.log('setting style number', styles[0].stylenumber);
        }
    }, [styles]);
    /**
     * Debounce fetch so it isn't called multiple times.
     */
    // const fetchAdminData = useCallback(
    //     _.debounce(function (criteria) {
    //         dispatch(fetchAggData());
    //     }, 100),
    //     [],
    // );

    useEffect(() => {
        const findItemIndex = (item) => {
            const idx = captures.findIndex((val) => val.id === item.id);
            return idx;
        };
        const keyHandler = (e) => {
            logger.log(e, ' KEY');
            if (e.keyCode === 65 && e.metaKey) {
                selectAll();
                return false;
            }
            if (selectedItems) {
                switch (e.code) {
                    case 'Numpad5':
                    case 'Numpad4':
                    case 'Numpad3':
                    case 'Numpad2':
                    case 'Numpad1':
                    case 'Digit5':
                    case 'Digit4':
                    case 'Digit3':
                    case 'Digit2':
                    case 'Digit1': {
                        // set the rating.
                        if (selectedItems && selectedItems.length) {
                            const data = { data: e.key };
                            onCaptureChange(CaptureEventType.RATING_CHANGE, data, selectedItems);
                        }

                        break;
                    }
                    case 'BracketLeft':
                    case 'BracketRight': {
                        // set the rating.
                        if (selectedItems && selectedItems.length) {
                            const data = { data: e.code === 'BracketLeft' ? FlagRating.FLAG_SELECT : FlagRating.FLAG_KILL };
                            onCaptureChange(CaptureEventType.STATUS_CHANGE, data, selectedItems);
                        }

                        break;
                    }
                    case 'ArrowRight': {
                        // set the next index.
                        // get the current index.
                        let idx = 0;
                        let setCompare = false;

                        if (comparisonItems && settings.layoutType === ApprovalLayoutTypes.COMPARE) {
                            let item = comparisonItems[selectedCompareFrame];
                            idx = findItemIndex(item);
                            setCompare = true;
                        } else {
                            idx = findItemIndex(selectedItems[0]);
                        }

                        if (idx > -1) {
                            if (idx + 1 < captures.length) {
                                const newItem = captures[idx + 1];
                                setSelectedItems([newItem]);
                                setSelectedItem({ ...newItem });

                                if (setCompare) {
                                    // set the selected frame with the selected capture.
                                    let compareFrames = [...comparisonItems];
                                    compareFrames[selectedCompareFrame] = newItem;
                                    setComparisonItems(compareFrames);
                                }
                            }
                        }
                        break;
                    }
                    case 'ArrowLeft': {
                        let idx = 0;
                        let setCompare = false;

                        if (comparisonItems && settings.layoutType === ApprovalLayoutTypes.COMPARE) {
                            let item = comparisonItems[selectedCompareFrame];
                            idx = findItemIndex(item);
                            setCompare = true;
                        } else {
                            idx = findItemIndex(selectedItems[0]);
                        }

                        if (idx > -1) {
                            if (idx - 1 > -1) {
                                const newItem = captures[idx - 1];
                                setSelectedItems([newItem]);
                                setSelectedItem({ ...newItem });

                                if (setCompare) {
                                    // set the selected frame with the selected capture.
                                    let compareFrames = [...comparisonItems];
                                    compareFrames[selectedCompareFrame] = newItem;
                                    setComparisonItems(compareFrames);
                                }
                            }
                        }
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        };

        window.addEventListener('keydown', keyHandler);

        // if (!aggData) {
        //     fetchAdminData();
        // }

        return () => {
            window.removeEventListener('keydown', keyHandler);
            // clear the image path cache
            imageLocHash.current = {};

            logger.log('CLEARING');
            clear();
        };
    }, [captures, selectedItems, comparisonItems, selectedCompareFrame]);

    const form = useForm({
        initialValues: {
            client: '',
            sessions: '',
            sessionId: ``,
            unpinned: false,
            star: '-1',
            sak: '-1',
            tags: [],
            machineId: '',
            styleNumber: '',
            colourTag: [],
        },

        validate: {
            // client: (value) => (value.length ? null : 'Please select a client'),
        },
    });

    const settingsChange = (type, data) => {
        logger.log(type, data);
        switch (type) {
            case ApprovalEventTypes.SETTINGS_CHANGE: {
                setApprovalSettings({ ...data });
                break;
            }
            default: {
                break;
            }
        }

        let urlParms = Object.fromEntries([...searchParams]);
        // const urlParms = new URLSearchParams(window.location.search);
        logger.log(urlParms, ' PARMS CHANGE');
        if (data.layoutType !== urlParms.layoutType) {
            // set the settings as part of the url.
            searchParams.set('layoutType', data.layoutType);

            urlParms.layoutType = data.layoutType;

            setSearchParams(urlParms);
        }
    };

    const openTagTalent = () => {
        openModal({
            title: 'Edit Guide',
            size: 'xl',
            children: (
                <>
                    <TagTalent
                        talents={[]}
                        // guide={guide}
                        // session={session}
                        // user={user}
                        // task={selectedTask}
                        onCancel={() => {
                            closeAllModals();
                        }}
                        // onMenuClick={onMenuClick}
                    />
                </>
            ),
            onClose: closeAllModals,
        });
    };
    const onImageEvent = (type, data, originalEvent) => {
        logger.log(type, data);
        var cItems, items;

        switch (type) {
            case CaptureEventType.IMAGE_CLICK: {
                logger.log(comparisonItems);
                //if(settings.layoutType !== ApprovalLayoutTypes.COMPARE){

                // if we already have one item

                // if control or shift is not held we set it to a single item
                if (originalEvent.metaKey) {
                    // if command held down we add the item to the array
                    // if it's not yet selected.
                    const obj = selectedItems.find((val) => val.id === data.id);
                    // if the object is already selected then we unselect it
                    if (obj) {
                        //items  = [...selectedItems];

                        items = _.remove([...selectedItems], (val) => {
                            return val.id !== data.id;
                        });

                        cItems = _.remove([...comparisonItems], (val) => {
                            return val.id !== data.id;
                        });
                    } else {
                        items = [...selectedItems, data];

                        cItems = comparisonItems;

                        // if(comparisonItems && comparisonItems.length){
                        //   cItems = [...comparisonItems];
                        //   cItems[selectedCompareFrame] = data;
                        // }else{
                        //   cItems = [data];
                        // }
                    }

                    // capture items remain the same;
                } else if (originalEvent.shiftKey) {
                    // if shift key we push all items from the last selected item
                    // get the index of the last element selected.
                    let lastObj = selectedItems ? selectedItems[selectedItems.length - 1] : null;
                    if (lastObj) {
                        let lastIdx = _.findIndex(captures, lastObj);
                        let currIdx = _.findIndex(captures, data);
                        let newItems = [...captures.slice(lastIdx + 1, currIdx + 1)];
                        items = _.concat(selectedItems, newItems);
                    }
                } else {
                    items = [data];

                    // setSelectedItems([data]);

                    // if no comparison items is selected
                    if (comparisonItems && comparisonItems.length) {
                        cItems = [...comparisonItems];
                        if (comparisonItems.length === 1) {
                            cItems[1] = data;
                        } else {
                            cItems[selectedCompareFrame] = data;
                        }
                    } else {
                        cItems = [data];
                    }

                    setSelectedItem({ ...data });

                    // searchParams.set('captureId',data.captureId);
                    // setSearchParams(searchParams);
                }

                setSelectedItems(items);
                setComparisonItems(cItems);

                // if(comparisonItems && comparisonItems.length){
                //   var items = [...comparisonItems];

                //   items[selectedCompareFrame] = data;
                //   setComparisonItems(items)
                // }else{
                //   setComparisonItems([data])
                // }

                break;
            }
            case CaptureEventType.IMAGE_DBL_CLICK: {
                setSelectedItem(data);
                // need to update the route
                settingsChange(ApprovalEventTypes.SETTINGS_CHANGE, { ...settings, layoutType: ApprovalLayoutTypes.SINGLE });
                break;
            }
            case ApprovalEventTypes.COMPARE_FRAME_CLICK: {
                setSelectedCompareFrame(data.index);
                break;
            }
            default: {
                break;
            }
        }
    };

    /**
     * Handler for image click
     */
    const onImageControlClick = useCallback(
        (type) => {
            logger.log('IMAGEW ZOOM CLICK');
            switch (type) {
                case ApprovalImageToolbarModes.ZOOM_RESET:
                case ApprovalImageToolbarModes.ZOOM_OUT:
                case ApprovalImageToolbarModes.ZOOM_IN: {
                    let props = { ...imageControlSettings, zoomDirection: type };
                    setImageControlSettings(props);
                    break;
                }
                default: {
                    break;
                }
            }
        },
        [imageControlSettings],
    );

    /**
     * Handler for Capture Change
     * @param {*} type
     * @param {*} data
     * @param {*} items
     */
    const onCaptureChange = (type, data, items) => {
        logger.log(type, data, items);
        switch (type) {
            case CaptureEventType.COLOUR_CHANGE: {
                const tmpItems = items.map((val) => {
                    return { colourTag: data.data, actionPart: val.actionPart, actionSort: val.actionSort };
                });
                dispatch(setCaptures(tmpItems, user));
                break;
            }
            case CaptureEventType.RATING_CHANGE: {
                const tmpItems = items.map((val) => {
                    return { star: data.data, actionPart: val.actionPart, actionSort: val.actionSort };
                });
                dispatch(setCaptures(tmpItems, user));
                break;
            }
            case CaptureEventType.STATUS_CHANGE: {
                const tmpItems = items.map((val) => {
                    return { sak: data.data, actionPart: val.actionPart, actionSort: val.actionSort };
                });
                dispatch(setCaptures(tmpItems, user));
                break;
            }
            case CaptureEventType.DOWNLOAD: {
                const tmpDate = moment(new Date()).format('DDMMYYYY_HHMMSS');
                const filename = `Batch_${tmpDate}`;
                const extension = data.type && data.type === 'eipURL' ? 'eip' : 'png';
                handleZipDownload(items, 'originalFilename', filename, data.type, extension);
                break;
            }

            case CaptureEventType.TAG_TALENT: {
                // openTagTalent()
                break;
            }

            default: {
                break;
            }
        }
    };

    /**
     * Search Submit handler
     * @param {*} values
     */
    const onSearchSubmit = (values, resetCursor = true) => {
        var criteria = { ...approvalFilter };

        if (!form.isValid() || !values) {
            return;
        }

        logger.log('here we go');
        // criteria.unpinned = false;

        // if the styleNumber is different to the current styleNumber then we clear the selected items
        if (criteria.styleNumber && criteria.styleNumber !== form.getInputProps('styleNumber').value) {
            // setSelectedItems([]);
            // setComparisonItems([]);
            // setSelectedItem(null);
        }
        var sessionId = '';
        if (values.sessionId && values.sessionId) {
            var val = values.sessionId;
            var sessionArr = val?.split('|');
            if (sessionArr.length > 1) {
                sessionId = sessionArr[1];
            }
        }
        criteria.sessionId = sessionId;
        if (criteria.sessionId && criteria.sessionId === 'undefined') {
            criteria.sessionId = null;
        }
        const company = getCompany(aggData);
        criteria.client = company?.code;
        // criteria.client = form.getInputProps('client') ? form.getInputProps('client').value : null;
        criteria.star = values.star ? values.star : -1;
        criteria.sak = values.sak ? values.sak : -1;
        criteria.tags = values.tags ? values.tags : null;
        criteria.styleNumber = values.styleNumber ? values.styleNumber : null;
        criteria.colourTag = form.getInputProps('colourTag') ? form.getInputProps('colourTag').value : [];
        // const startDate = moment().year(2000);

        if (criteria.sessionId === SessionTypes.UNKOWN) {
            criteria.machineId = criteria.sessionId;
            criteria.sessionId = null;
        }

        criteria.machineId = form.getInputProps('machineId') ? form.getInputProps('machineId').value : null;

        // if we have any of the other criteria set and the cataglogue is latest then we clear the catalogue.
        if ((criteria.sessionId && criteria.sessionId.length) || criteria.star > -1 || criteria.sak > -1 || criteria.tags || criteria.machineId) {
            if (criteria.catalogue.label === 'Latest') criteria.catalogue = null;
        }

        buildSearchParams(criteria);

        setLoading(true);
        setIsSearching(true);

        const cursor = resetCursor ? '' : searchCursor;
        debouncedCaptureFetch(criteria, cursor);

        // navigate({
        //   search: `?${str}`,
        // });
        //dispatch(fetchCaptures(criteria,user))
    };

    // const buildSearchString = (criteria) => {
    //     const keys = Object.keys(criteria);
    //     var str = '';
    //     var delim = '';
    //     keys.forEach((key, idx) => {
    //         var val = criteria[key];

    //         if (typeof val === 'object') {
    //             if (key === 'catalogue' && val.label.length) {
    //                 val = JSON.stringify(val);
    //             }
    //         } else if (val) {
    //             val = val.toString();
    //         }

    //         // lowercase the first.
    //         logger.log(key, val);
    //         if (val && val.length) {
    //             str += `${delim}${key}=${val}`;
    //             delim = '&';
    //         }
    //     });
    //     return str;
    // };

    const buildSearchParams = (criteria) => {
        filterKeys.forEach((key) => {
            if (key !== 'catalogue') {
                if (criteria[key] && criteria[key] !== 'undefined') searchParams.set(key, criteria[key]);
                else searchParams.delete(key);
            }
        });

        setSearchParams(searchParams);
    };

    /**
     * Debounced fetch for captures
     */
    // const debouncedCaptureFetch = useCallback(
    //   (criteria) => {
    //       return debounce(dispatch(fetchCaptures(criteria,user)), 3000)
    //   }
    // , []);

    const debouncedCaptureFetch = useCallback(
        _.debounce(function (criteria, cursor) {
            dispatch(fetchCaptures(criteria, user, 1, cursor));
        }, 100),
        [],
    );

    const debouncedLatestCaptureFetch = useCallback(
        _.debounce(function (criteria) {
            dispatch(fetchLatestCaptures());
        }, 100),
        [],
    );

    /**
     * Effect to look after the search params change, this drives the filtering of the captures and lets
     * The route handle the filtering
     */
    useEffect(() => {
        // let urlParms = Object.fromEntries([...searchParams]);
        let urlParms = Object.fromEntries(new URLSearchParams(location.search));

        if (!urlParms.catalogue) {
            //urlParms.catalogue = {label:'',value:{since:null,until:null}};
        } else {
            urlParms.catalogue = JSON.parse(urlParms.catalogue);
        }
        // let client = '';

        // if(urlParms.sessionId){
        //     let splitArr = urlParms.sessionId.split('-');
        //     if(splitArr.length)client = splitArr[0];
        // }
        // pass in the client
        const company = getCompany(aggData);
        const criteria = new CaptureFilterModel({ ...urlParms, client: company?.code });

        // has the filter changed
        var filterChanged = hasFilterChanged(criteria);
        logger.log(filterChanged, 'filterChanged', criteria);
        // var filterChanged = true;
        if (filterChanged || !hasFetchedStyles.current) {
            logger.log(searchParams);

            logger.log(company, 'company');
            // set the form values.
            form.setFieldValue('sessionId', urlParms.sessionId ? `session|${urlParms.sessionId}` : '');
            form.setFieldValue('star', urlParms.star ? urlParms.star : '-1');
            form.setFieldValue('sak', urlParms.sak ? urlParms.sak : '-1');
            form.setFieldValue('machineId', urlParms.machineId);
            form.setFieldValue('tags', urlParms.tags);
            form.setFieldValue('styleNumber', urlParms.styleNumber);
            form.setFieldValue('colourTag', urlParms.colourTag ?? []);
            // form.setFieldValue('client', urlParms.client ? urlParms.client : '');
            // change to always grab this client
            // if(aggData && aggData.clients && aggData.clients.length){
            //     form.setFieldValue('client',aggData.clients[0].code);
            // }

            form.setFieldValue('client', company?.code);
            // Update the filter
            setFilter(criteria);

            // we check that there are other default values else the default is only layoutType.
            if (Object.keys(urlParms).length > 1) {
                setLoading(true);
                setIsSearching(true);

                //dispatch(fetchCaptures(criteria,user));
                debouncedCaptureFetch(criteria, '');
            } else if (Object.keys(urlParms).length === 1) {
                setLoading(true);
                setIsSearching(true);
                // we grab the latest captures
                debouncedLatestCaptureFetch();
            }

            hasFetchedStyles.current = true;
        }

        // check for layout change
        if (urlParms.layoutType && urlParms.layoutType !== settings.layoutType) {
            setApprovalSettings({ ...settings, layoutType: urlParms.layoutType });
        }
    }, [location]);

    // on change of the session we need to get the style numbers
    useEffect(() => {
        if (form.values.sessionId && form.values.sessionId.length) {
            logger.log('fetching styles');
            const sessionId = form.getInputProps('sessionId') ? form.getInputProps('sessionId').value.split('|')[1] : null;
            logger.log(sessionId, 'sessionId');
            dispatch(getStyles({ sessionId }));
        }
    }, [form.values.sessionId]);

    /**
     * Determine whether the passed in filter has changed from the previous.
     * @param {filterCriteria} obj
     * @returns
     */
    const hasFilterChanged = useCallback(
        (obj) => {
            var hasChanged = false;
            for (let i = 0; i < filterKeys.length; i++) {
                const key = filterKeys[i];
                if (obj[key] && approvalFilter[key] && approvalFilter[key].toString() !== obj[key].toString()) {
                    hasChanged = true;
                    break;
                }
            }

            if (obj.catalogue) {
                const objCat = isPureObject(obj.catalogue) ? obj.catalogue : JSON.parse(obj.catalogue);
                const appCat = isPureObject(approvalFilter.catalogue) ? approvalFilter.catalogue : JSON.parse(approvalFilter.catalogue);
                if (objCat.label !== appCat.label) {
                    hasChanged = true;
                }
            }

            return hasChanged;
        },
        [approvalFilter],
    );

    const onCollectionClick = (val) => {
        logger.log(val);

        // let filter = {...approvalFilter};
        // filter.catalogue = {...val};

        // logger.log(filter)

        //   val = JSON.stringify(val);

        // let params = [...searchParams];

        // we clear the other filters if we have selected latest
        if (val && val.label === 'Latest') {
            onClearFilterClick();
            navigate({
                search: `?catalogue=${encodeURI(JSON.stringify(val))}`,
            });
            // searchParams.set('catalogue',JSON.stringify(val));
        } else {
            searchParams.set('catalogue', JSON.stringify(val));
            setSearchParams(searchParams);
        }

        // const str = buildSearchString(filter);

        // navigate({
        //   search: `?${str}`,
        // });
    };

    const onClearFilterClick = () => {
        logger.log('clearing filter');
        // let vals = {};
        // filter.catalogue = null;
        // vals.star = -1;
        // vals.sak = -1;
        // vals.tags = [];
        // vals.sessionId = '';

        // form.setValues(vals)
        form.reset();
        setSearchParams([]);
    };

    /**
     * Handler for the close click
     */
    const onCloseClick = () => {
        const path = previousRoute ? previousRoute : '/warehouse';
        navigate(path);
    };

    /**
     * Capture Change
     */
    useEffect(() => {
        // if they've selected an image.
        const captureId = searchParams.get('captureId');
        if (captures && captures.length) {
            if ((captureId && selectedItem && captureId !== selectedItem.id) || (captureId && !selectedItem)) {
                const capture = captures.find((val) => val.id === captureId);

                if (capture) {
                    // TODO work out why this is taking so long setting state causes bottom tray to flash
                    logger.log(' SETTING SELECTED CAPTURE');
                    setSelectedItem({ ...capture });
                    setSelectedItems([capture]);
                    searchParams.delete('captureId');
                    // setSearchParams(searchParams);
                }
            }
        }
    }, [captures]);

    const loadNextPage = () => {
        onSearchSubmit(null, false);
    };

    // Create breadcrumb items for current session and style
    const crumbItems = () => {
        const items = [{ title: '', route: '/approval', search: '' }];

        // Add session if available
        if (approvalFilter.sessionId) {
            const session = sessionData.find((s) => s.sessionId === approvalFilter.sessionId);
            if (session) {
                items.push({
                    title: `${session.code}${session.name ? ` - ${session.name}` : ''}`,
                    route: `/approval?sessionId=${session.id}`,
                    search: `sessionId=${session.id}`,
                    type: 'session',
                });
            }
        }

        // Add style if available
        if (searchParams.get('styleNumber')) {
            items.push({
                title: `${searchParams.get('styleNumber')}`,
                route: `/approval?${approvalFilter.sessionId ? `sessionId=${approvalFilter.sessionId}&` : ''}styleNumber=${searchParams.get('styleNumber')}`,
                search: `${approvalFilter.sessionId ? `sessionId=${approvalFilter.sessionId}&` : ''}styleNumber=${searchParams.get('styleNumber')}`,
                type: 'style',
            });
        }

        return items;
    };

    useEffect(() => {
        const getData = async () => {
            try {
                const sessionsData = await getSessions({}, '');
                setSessionData(sessionsData);
                var tmpTarget = { sessionId: null, styleNumber: null };
                const tmpSessionId = searchParams.get('sessionId');
                const tmpStyleNumber = searchParams.get('styleNumber');
                // Set pending scroll if we have a selected session
                if (tmpSessionId) {
                    const session = sessionsData.find((s) => s.sessionId === tmpSessionId);
                    if (session) {
                        tmpTarget = { sessionId: session.id };
                    }
                }
                if (tmpStyleNumber) {
                    tmpTarget.styleNumber = tmpStyleNumber;
                }
                setTimeout(() => {
                    // setScrollToTarget(tmpTarget);
                    // setScrollToTarget(scrollToTarget);
                    handleScrollToSession(tmpTarget.sessionId, tmpTarget.styleNumber);
                }, 1000);

                dispatch(fetchTalent({}));
            } catch (error) {
                logger.error('Error fetching sessions:', error);
            }
        };

        getData();
    }, []);

    // Handle pending scroll when scroll function becomes available

    // Utility functions for manual scrolling
    const handleScrollToSession = useCallback((sessionId, styleNumber) => {
        setScrollToTarget({ sessionId, styleNumber });

        // Clear the target after a short delay to allow for re-triggering
        setTimeout(() => setScrollToTarget(null), 100);
    }, []);

    const handleScrollToStyle = useCallback((sessionId, styleId) => {
        setScrollToTarget({ sessionId, styleId });

        // Clear the target after a short delay to allow for re-triggering
        setTimeout(() => setScrollToTarget(null), 100);
    }, []);

    return (
        <AppShell
            padding='md'
            className={[settings.infoOpen ? `${classes.shell} info-open` : classes.shell, settings.filterOpen ? `${classes.shell} filter-open` : '']}
            navbar={
                <Navbar width={{ base: 350 }} height={'100%'} className='filter-panel'>
                    <form onSubmit={form.onSubmit((values) => logger.log(values))}>
                        {/* <Box m={'sm'}>
                            <Input
                                className={classes.keywordSearch}
                                icon={<IconSearch size={16} />}
                                placeholder='Keyword'
                                rightSection={
                                    <div className='unhightable'>
                                        <Text size={'xs'}>
                                            {' '}
                                            <Kbd>
                                                <span>Press</span> ↵
                                            </Kbd>
                                        </Text>
                                    </div>
                                }
                            />
                        </Box>

                        <Divider /> */}

                        <FilterPanel
                            approvalFilter={approvalFilter}
                            aggData={aggData}
                            isSearching={isSearching}
                            form={form}
                            onSubmit={(values, resetCursor = true) => onSearchSubmit(values, resetCursor)}
                            onCollectionClick={onCollectionClick}
                            onClearClick={() => onClearFilterClick()}
                            styles={styles}
                            sessions={sessionData}
                            scrollToTarget={scrollToTarget}
                        />
                    </form>
                </Navbar>
            }
            header={
                <Header height={60} p='xs' sx={{ display: 'flex', width: '100%' }}>
                    {/* <Center> */}
                    <Group position='apart' sx={{ width: '100%' }}>
                        {/* <Crumbline /> */}
                        <Group>
                            <UnstyledButton
                                onClick={() => {
                                    onCloseClick();
                                }}>
                                <Group>
                                    <IconArrowLeft />
                                    <Title order={5} className='unhightable'>
                                        Capture Management
                                    </Title>
                                </Group>
                            </UnstyledButton>
                            {/* Breadcrumb showing current session and style */}
                            <Group>
                                {/* <Crumbline items={crumbItems} /> */}

                                {crumbItems()?.map((item, index) => (
                                    // <React.Fragment key={index}>
                                    // {item.type === 'session' &&
                                    //        <Select className={classes.crumbSelect} {...form.getInputProps('sessionId')} searchable data={sessionData.map(s => s.id)} onChange={(value) => {

                                    //         form.setFieldValue('sessionId', `${value}`);

                                    //         // Create updated form values with the new sessionId
                                    //         const updatedValues = { ...form.values, sessionId: value, styleNumber: null };
                                    //         onSearchSubmit(updatedValues, true);
                                    //         setTimeout(() => {
                                    //             // allow time to scroll
                                    //             // scroll to the session
                                    //             handleScrollToSession(value);
                                    //         }, 1000); // Reduced timeout since we don't need to wait for form state
                                    //        }} />
                                    //     }
                                    //     {item.type === 'style' && styles?.length &&
                                    //         <Select className={classes.crumbSelect} {...form.getInputProps('styleNumber')} searchable data={styles.map(s => s.targetSort)} onChange={(value) => {
                                    //             logger.log(value);
                                    //             form.setFieldValue('styleNumber', value);
                                    //             const updatedValues = { ...form.values, styleNumber: value };

                                    //             setTimeout(() => {
                                    //                 onSearchSubmit(updatedValues, true);
                                    //             }, 100); // Reduced timeout since we don't need to wait for form state
                                    //         }} />
                                    //     }
                                    //     {index < crumbItems().length - 1? <IconChevronRight size={16} />:null}
                                    // </React.Fragment>
                                    <Popover key={index} position='bottom-end' withArrow withinPortal zIndex={1000}>
                                        <Popover.Target>
                                            <Group sx={{ cursor: 'pointer' }}>
                                                <Text size='sm' weight={index === crumbItems.length - 1 ? 'normal' : 500} key={index}>
                                                    {item.title}
                                                </Text>
                                                {index > 0 && (
                                                    <ThemeIcon size='xs' variant='light'>
                                                        <IconSelector size={16} />
                                                    </ThemeIcon>
                                                )}
                                                {index < crumbItems().length - 1 ? <IconChevronRight size={16} opacity={0.5} /> : null}
                                            </Group>
                                        </Popover.Target>
                                        <Popover.Dropdown>
                                            {item.type === 'session' && (
                                                <Select
                                                    style={{ width: 400 }}
                                                    itemComponent={SelectItem}
                                                    {...form.getInputProps('sessionId')}
                                                    searchable
                                                    data={sessionData.map((val) => {
                                                        return { label: val.name ? `${val.code} - ${val.name}` : '', value: val.id, description: val.name, code: val.code };
                                                    })}
                                                    onChange={(value) => {
                                                        form.setFieldValue('sessionId', `${value}`);

                                                        // Create updated form values with the new sessionId
                                                        const updatedValues = { ...form.values, sessionId: value, styleNumber: null };
                                                        onSearchSubmit(updatedValues, true);
                                                        setTimeout(() => {
                                                            // allow time to scroll
                                                            // scroll to the session
                                                            handleScrollToSession(value);
                                                        }, 1000); // Reduced timeout since we don't need to wait for form state
                                                    }}
                                                />
                                            )}
                                            {item.type === 'style' && styles?.length && (
                                                <Select
                                                    {...form.getInputProps('styleNumber')}
                                                    searchable
                                                    data={styles.map((s) => s.targetSort)}
                                                    onChange={(value) => {
                                                        logger.log(value);
                                                        form.setFieldValue('styleNumber', value);
                                                        const updatedValues = { ...form.values, styleNumber: value };

                                                        setTimeout(() => {
                                                            onSearchSubmit(updatedValues, true);
                                                        }, 100); // Reduced timeout since we don't need to wait for form state
                                                    }}
                                                />
                                            )}
                                        </Popover.Dropdown>
                                    </Popover>
                                ))}
                            </Group>
                        </Group>

                        <Group>
                            <ActionIcon onClick={() => setInfoShown(true)} variant='default'>
                                <IconInfoCircle size={18} />
                            </ActionIcon>
                            <ActionIcon onClick={toggle} variant='default'>
                                <IconArrowsMaximize size={18} />
                            </ActionIcon>
                            <Button
                                size='xs'
                                variant='outline'
                                onClick={() => {
                                    onCloseClick();
                                }}>
                                Close
                            </Button>
                        </Group>
                    </Group>
                    {/* </Center> */}
                </Header>
            }
            styles={(theme) => ({
                main: {
                    transition: 'padding .1s ease-in-out',
                    minHeight: 'calc(100vh - 60px)',
                    marginLeft: `0 !important`,
                    marginRight: '0 !important',
                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
                    paddingTop: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingBottom: 0,
                },
            })}>
            <ModalsProvider>
                <div style={{ height: 'calc(100%)', width: '100%', flexWrap: 'nowrap', display: 'flex', flexGrow: 0, gap: 0 }}>
                    <Stack className={classes.container} sx={{ height: 'calc(100%)' }}>
                        <Toolbar
                            settingsChange={(type, data) => settingsChange(type, data)}
                            settings={settings}
                            selectedItems={selectedItems}
                            onImageControlClick={onImageControlClick}
                            selectedCompareFrame={selectedCompareFrame}
                            onCaptureChange={onCaptureChange}
                        />

                        <MainPanel
                            settings={settings}
                            onImageEvent={onImageEvent}
                            captures={captures}
                            comparisonItems={comparisonItems}
                            selectedItem={selectedItem}
                            selectedItems={selectedItems}
                            selectedCompareFrame={selectedCompareFrame}
                            imageControlSettings={imageControlSettings}
                            onSettingsChange={settingsChange}
                            onCaptureChange={onCaptureChange}
                            imageLocHash={imageLocHash}
                            isSearching={isSearching}
                            searchCursor={searchCursor}
                            loadNextPage={loadNextPage}
                            showLoading={(captures.length === 0 && isLoading) || (appLoading && captures.length === 0)}
                            talents={talents}
                        />

                        {settings.layoutType !== ApprovalLayoutTypes.GRID ? (
                            <BottomTray
                                captures={captures}
                                onImageEvent={onImageEvent}
                                selectedItems={selectedItems}
                                comparisonItems={comparisonItems}
                                selectedCompareFrame={selectedCompareFrame}
                                imageLocHash={imageLocHash}
                                onCaptureChange={onCaptureChange}
                                showComapirsonSelection={settings.layoutType === ApprovalLayoutTypes.COMPARE}
                                isSearching={isSearching}
                                searchCursor={searchCursor}
                                loadNextPage={loadNextPage}
                                showLoading={captures.length === 0 && isLoading}
                            />
                        ) : null}
                    </Stack>

                    <InfoPanel selectedItem={selectedItem} talents={talents} />
                </div>
            </ModalsProvider>
            <Modal opened={infoShown} centered onClose={() => setInfoShown(false)} title='Capture Management Shortcuts'>
                <Shortcuts />
            </Modal>
        </AppShell>
    );
};

////////////////////////////////
//
// SELECTORS
// memoize the selection for sorted captures
//
////////////////////////////////

const mapStateToProps = (state, props) => {
    return {
        user: state.auth.user,
        captures2: state.approvalReducer.captures,
        selectedItem: state.approvalReducer.selectedItem,
        selectedItems: state.approvalReducer.selectedItems,
        comparisonItems: state.approvalReducer.comparisonItems,
        selectedCompareFrame: state.approvalReducer.selectedCompareFrame,
        approvalFilter: state.approvalReducer.approvalFilter,
        settings: state.approvalReducer.settings,
        success: state.approvalReducer.success,
        error: state.approvalReducer.error,
        isLoading: state.approvalReducer.loading,
        action: state.approvalReducer.action,
        captures: selectSortedCaptures(state.approvalReducer),
        aggData: state.appReducer.aggData,
        appLoading: state.appReducer.loading,
        previousRoute: state.appReducer.previousRoute,
        searchCursor: state.approvalReducer.searchCursor,
        styles: state.approvalReducer.styles,
        talents: state.approvalReducer.talents,
    };
};

// const mapStateToProps = (state, props) => {
//     return {
//         user: state.auth.user,
//         captures2: state.approvalReducer.present.captures,
//         selectedItem: state.approvalReducer.present.selectedItem,
//         selectedItems: state.approvalReducer.present.selectedItems,
//         comparisonItems: state.approvalReducer.present.comparisonItems,
//         selectedCompareFrame: state.approvalReducer.present.selectedCompareFrame,
//         approvalFilter: state.approvalReducer.present.approvalFilter,
//         settings: state.approvalReducer.present.settings,
//         success: state.approvalReducer.present.success,
//         error: state.approvalReducer.present.error,
//         isLoading: state.approvalReducer.present.loading,
//         action: state.approvalReducer.present.action,
//         captures: selectSortedCaptures(state.approvalReducer.present),
//         aggData: state.appReducer.aggData,
//         appLoading: state.appReducer.loading,
//         previousRoute: state.appReducer.previousRoute,
//         searchCursor: state.approvalReducer.present.searchCursor,
//         styles: state.approvalReducer.present.styles,
//     };
// };

const mapDispatch = {
    setApprovalSettings,
    setSelectedItems,
    setSelectedItem,
    setComparisonItems,
    setSelectedCompareFrame,
    setFilter,
    setLoading,
    setUser,
};

export default connect(mapStateToProps, mapDispatch)(ApprovalModule);

export const ApprovalEventTypes = {
    SETTINGS_CHANGE: 'settingsChange',
    COMPARE_FRAME_CLICK: 'compareFrameClick',
};

export const SortFields = {
    JOB_ID: 'sessionId',
    MACHINE_ID: 'machineId',
    FILE_NAME: 'originalFilename',
    RATING: 'star',
    DATE: 'actionSort',
};

export const CaptureSortFields = [
    { label: 'Capture Date', value: SortFields.DATE },
    { label: 'Session', value: SortFields.JOB_ID },
    { label: 'Machine', value: SortFields.MACHINE_ID },
    { label: 'File Name', value: SortFields.FILE_NAME },
    { label: 'Rating', value: SortFields.RATING },
];

const SelectItem = forwardRef(({ label, value, description, code, ...others }, ref) => {
    const displayLabel = label.split('|')[0];

    return (
        <div ref={ref} {...others}>
            <Group noWrap>
                <div>
                    <Text size='sm'>{code}</Text>
                    <Text size='xs' opacity={0.65}>
                        {description}
                    </Text>
                </div>
            </Group>
        </div>
    );
});
